/**
 * API Configuration
 * Single source of truth for API settings
 */

// API Endpoints - organized by module
export const API_ENDPOINTS = {
  objectStorage: {
    upload: "/file/upload",
    delete: "/file/delete",
  },
  auth: {
    signup: "/auth/signup",
    checkLogin: "/auth/login/check?identifier=",
    verifyLogin: "/auth/login/verify",
    logout: "/auth/logout",
    otpGenerate: "/auth/otp/generate",
    otpVerify: "/auth/otp/verify",
    forgotPassword: "/auth/forget",
    resetPassword: "/auth/reset",
    refreshToken: "/auth/refreshToken",
    googleOAuth: "/auth/oauth/google",
    googleOAuthCallback: "/auth/oauth/google/callback",
    googleOAuthAgentCallback: "/auth/oauth/google/agent/callback",
    microsoftOAuth: "/auth/oauth/microsoft",
    microsoftOAuthCallback: "/auth/oauth/microsoft/callback",
    microsoftOAuthAgentCallback: "/auth/oauth/microsoft/agent/callback",
    verifyTurnstile: "/auth/turnstile/submit-form",
    validateInvitation: "/auth/invitations/validate",
    acceptInvitation: "/auth/invitations/accept",
    agentSignup: "/auth/agent-signup",
    generateToken: "/auth/generate-token",
    validateLink: "/auth/validate",
  },
  user: {
    getOrganizations: (email: string) => `/user/${encodeURIComponent(email)}`,
    profileEnquiry: "/user/profile-enquiry",
    updateProfile: "/user/update-profile",
  },
  whatsapp: {
    accountDetails: "/whatsapp/account-details",
    verifyAccount: "/whatsapp/test-wba-account",
    updateAccount: "/whatsapp/update-wba-details",
    phoneNumbers: "/whatsapp/phone-numbers",
    phoneNumberStatuses: "/whatsapp/phone-numbers/status",
    onboarding: "auth/whatsapponboarding",
  },
  whatsappManagement: {
    qrCodes: {
      getAll: "/message_qrdls/getall",
      create: "/message_qrdls/create",
      update: "/message_qrdls/update",
      delete: "/message_qrdls/deleteqrcode",
    },
    consent: {
      getPolicy: "/whatsapp/consent/policy",
      upsertPolicy: "/whatsapp/consent/policy",
      getKeywords: "/whatsapp/consent/keywords",
      addKeyword: "/whatsapp/consent/keywords",
      deleteKeyword: (keyword: string) =>
        `/whatsapp/consent/keywords/${encodeURIComponent(keyword)}`,
      upsertAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/messages/${consentType}`,
      getAutoReplyMessage: (consentType: string) =>
        `/whatsapp/consent/reply/${consentType}`,
      getAllMessages: "/whatsapp/consent/reply",
      optedOutContacts: "/consents/opted-out",
    },
    widget: {
      createOrUpdate: "/whatsapp-button-config/create-update",
      getAllByCIN: "/whatsapp-button-config/cin/",
      getById: "/whatsapp-button-config/get/",
      delete: "/whatsapp-button-config/button/",
    },
  },
  templates: {
    base: "/templates",
    byId: (templateId: number | string) => `/templates/${templateId}`,
    submitToMeta: "/templates/submit-to-meta",
    delete: "/templates/delete",
    uploadMediaResumable: "/upload-media/resumable",
    uploadMediaWhatsapp: "/upload-media",
    aiGenerate: "/templates/generate",
    library: "/template-library",
    syncStatus: "/templates/sync-status",
  },
  emailTemplates: {
    content: (templateId: number | string) =>
      `/email/templates/${templateId}/content`,
    publish: (templateId: number | string) =>
      `/email/templates/${templateId}/publish`,
    test: (templateId: number | string) =>
      `/templates/${templateId}/test`,
  },
  ctwaAds: {
    fetchMapping: "/instagram-ads/CTWAMapping/fetch",
    createOrUpdateMapping: "/instagram-ads/CTWAMapping/create",
    listPages: "/instagram-ads/pages",
    listAdAccounts: "/instagram-ads/ad-accounts",
    listCampaigns: "/instagram-ads/campaigns/list",
    insights: "/instagram-ads/insights",
    campaignRelatedServices: "/instagram-ads/campaign-related-services",
    targetSearch: "/instagram-ads/target-search",
    createCampaign: "/instagram-ads/campaign",
    createAdSet: "/instagram-ads/adset",
    createAdCreative: "/instagram-ads/adcreative",
    createAd: "/instagram-ads/ad",
    uploadAdMedia: "/instagram-ads/ad-images/upload",
    uploadAdVideo: "/instagram-ads/ad-videos/upload",
    oauthAccessToken: "/instagram-ads/oauth/access-token",
    delete: "/instagram-ads/delete",
  },
  userManagement: {
    roles: {
      base: "/roles",
      byId: (roleId: number | string) => `/roles/${roleId}`,
      currentUserPermissions: "/me/permissions",
    },
    permissions: {
      system: "/permissions",
      rolePermissions: "/role-permissions",
      updateBulk: "/role-permissions/bulk",
    },
    teams: {
      base: "/teams",
      byId: (teamId: number | string) => `/teams/${teamId}`,
      users: (teamId: number | string) => `/teams/${teamId}/users`,
      manageUsers: (teamId: number | string) => `/teams/${teamId}/users/manage`,
    },
    users: {
      list: "/users/list",
      superAdmin: "/users/super-admin",
      invitations: "/users/invitations",
      invitationById: (invitationId: number | string) =>
        `/users/invitations/${invitationId}`,
      resendInvitation: (invitationId: number | string) =>
        `/users/invitations/${invitationId}/resend`,
      updateStatus: (userId: number | string, status: string) =>
        `/user/${userId}/status?status=${status}`,
    },
    projects: {
      base: "/projects",
      byId: (projectId: number | string) => `/projects/${projectId}`,
      users: "/project/users",
      userProjects: "/user/projects/list",
      members: (projectId: number | string) => `/projects/${projectId}/members`,
      manageMembers: "/projects/members",
      changeUserRole: "/project/user/role/change",
    },
  },
  messageRules: {
    enquiry: "/rule/enquiry",
    create: "/rule/create",
    keywords: "/rule/keywords",
    quickRepliesEnquiry: "/web/quick-replies-enquiry",
  },
  propertyManagement: {
    groups: "/groups",
    fields: "/fields",
    archiveProperty: "/properties/archive",
    deleteProperty: "/properties",
    bulkAssignToGroup: "/properties/groups/bulk",
    entityUsages: "/entity-usages",
  },
  crmOrders: {
    create: (projectId: number | string) => `orders/${projectId}/order`,
    byId: (projectId: number | string, orderId: number | string) =>
      `orders/${projectId}/order/${orderId}`,
    list: (projectId: number | string) => `orders/${projectId}/orders`,
    bulkStatus: (projectId: number | string) =>
      `orders/order/bulk-status/${projectId}`,
    bulkHardDelete: (projectId: number | string) =>
      `orders/bulk-hard-delete/${projectId}`,
    bulkUpsertProperty: (projectId: number | string) =>
      `orders/order/bulk-upsert-property/${projectId}`,
    property: (projectId: number | string) =>
      `orders/order/property/${projectId}`,
  },
  entityUsages: {
    base: "/entity-usages",
  },
  conversations: {
    base: "/conversations",
    byId: (conversationId: number | string) =>
      `/conversations/${conversationId}`,
    history: (conversationId: number | string) =>
      `/conversations/${conversationId}/history`,
    status: (conversationId: number | string) =>
      `/conversations/${conversationId}/status`,
    assignAgent: (conversationId: number | string) =>
      `/conversations/${conversationId}/assign-agent`,
    comments: (conversationId: number | string) =>
      `/conversations/${conversationId}/comments`,
    commentById: (
      conversationId: number | string,
      commentId: number | string,
    ) => `/conversations/${conversationId}/comments/${commentId}`,
    deleteComment: (commentId: number | string) =>
      `/conversations/comments/${commentId}`,
    messages: "/messages",
    sendMessage: "/messages",
    typingIndicator: "/messages/whatsapp/typing-indicator",
    unreadReset: (conversationId: number | string) =>
      `/conversations/${conversationId}/unread-reset`,
    messageStatusEvents: (messageId: number | string) =>
      `/messages/${messageId}/status-events`,
    messageStatusEventsByQuery: "/messages/status-events",
    counts: "/conversations/counts",
  },
  emailSettings: {
    footers: "/v1/email-footers",
    domains: "/email/domains",
    domainById: (domainId: number | string) => `/email/domains/${domainId}`,
    verifyDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/verify`,
    activateDomain: (domainId: number | string) =>
      `/email/domains/${domainId}/activate`,
    deleteDomain: (domainId: number | string) => `/email/domains/${domainId}`,
    senders: "/email/senders",
    senderById: (senderId: number | string) => `/email/senders/${senderId}`,
    setDefaultSender: (senderId: number | string) =>
      `/email/senders/${senderId}/default`,
  },
  schema: {
    views: {
      base: "/views",
      byObjectCode: (objectCode: string) => `/views/${objectCode}`,
      byId: (viewId: number | string) => `/views/${viewId}`,
    },
    kanban: {
      cardConfig: (objectCode: string) =>
        `kanban/${encodeURIComponent(objectCode)}/card-config`,
      cardConfigProperties: (objectCode: string) =>
        `kanban/${encodeURIComponent(objectCode)}/card-config/properties`,
    },
    recordViews: {
      base: "record-views",
      byId: (recordViewId: number | string) => `record-views/${recordViewId}`,
      cards: "record-views/cards",
      cardRegistry: "record-views/cards/registry",
      upsert: "record-views/upsert",
    },
  },
  activities: {
    notes: "activities/notes",
    emailsLog: "activities/emailsLog",
    emails: "activities/emails",
    base: "activities",
    comments: "activities/comments",
    associations: "activities/associations",
    history: "activities/history",
    calls: "activities/calls",
    tasks: "activities/tasks",
    meetingLog: "activities/meetingLog",
    meetings: "activities/meetings",
    associationSchemas: "association-schemas",
  },
  associations: {
    base: "/associations",
    bulkCreate: "/associations/bulk-create",
    schemas: "/association-schemas",
  },
  campaigns: {
    search: "/campaigns/search",
    base: "/campaigns",
    byId: (campaignId: number | string) => `/campaigns/${campaignId}`,
    audiencePreview: "/campaigns/audience/preview",
    audience: (campaignId: number | string) =>
      `/campaigns/${campaignId}/audience`,
    hardDelete: (campaignId: number | string) =>
      `/campaigns/${campaignId}/hard`,
    recipients: (campaignId: number | string) =>
      `/campaigns/${campaignId}/recipients`,
    insights: (campaignId: number | string) =>
      `/campaigns/${campaignId}/insights`,
    executions: (campaignId: number | string) =>
      `/campaigns/${campaignId}/executions`,
  },
  exports: {
    create: "/exports",
    list: "/exports",
    schedule: (exportId: number | string) => `/exports/${exportId}/schedule`,
  },
  subscription: {
    plans: "/plans",
    planById: (planId: number | string) => `/plans/${planId}`,
    subscriptions: "/subscriptions",
    subscriptionById: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}`,
    changeSubscriptionPlan: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/change-plan`,
    cancelSubscription: (subscriptionId: number | string) =>
      `/subscriptions/${subscriptionId}/cancel`,
    currentSubscription: "/subscriptions/current",
  },
  pipelines: {
    base: "/pipelines",
    byId: (pipelineId: number | string) => `/pipelines/${pipelineId}`,
    stages: (pipelineId: number | string) => `/pipelines/${pipelineId}/stages`,
  },
  flowBuilder: {
    chatbots: {
      base: "/chatbot",
      byId: (chatbotId: number | string) => `/chatbot/${chatbotId}`,
      triggers: (chatbotId: number | string) =>
        `/chatbot/${chatbotId}/triggers`,
      triggerExists: "/chatbot/triggers/exists",
      settings: "/chatbot/settings",
      settingsStatus: (settingsId: number | string) =>
        `/chatbot/settings/${settingsId}/status`,
    },
    flows: {
      base: "/chatbot/flows",
      byId: (flowId: number | string) => `/chatbot/flows/${flowId}`,
      status: "/chatbot/flows/status",
      graph: "/chatbot/flows/graph",
      graphById: (flowId: number | string) => `/chatbot/flows/${flowId}/graph`,
    },
    variables: {
      base: "/chatbot/flow-variables",
      byKey: (variableKey: string) =>
        `/chatbot/flow-variables/${encodeURIComponent(variableKey)}`,
    },
    integrations: {
      platforms: "/integration-platforms",
      actions: "/integration-platforms/actions",
      actionSchema: "/integration-platforms/actions/schema",
      integrationStatus: "/integration-platforms/integration/status",
    },
  },
  flowBuilderAi: {
    capabilities: "/flow-builder/ai/capabilities",
    validate: "/flow-builder/ai/validate",
    generate: "/flow-builder/ai/generate",
    generateStart: "/flow-builder/ai/generate/start",
    generateStatus: (jobId: string) =>
      `/flow-builder/ai/generate/status/${encodeURIComponent(jobId)}`,
    generateStream: (jobId: string) =>
      `/flow-builder/ai/generate/stream/${encodeURIComponent(jobId)}`,
    generateCancel: (jobId: string) =>
      `/flow-builder/ai/generate/cancel/${encodeURIComponent(jobId)}`,
    repair: "/flow-builder/ai/repair",
  },
  triggers: {
    base: "/triggers",
    byId: (triggerId: number | string) => `/triggers/${triggerId}`,
    actions: "/triggers/actions",
    status: "/triggers/status",
    logs: "/triggers/logs",
  },
  campaignScheduler: {
    schedule: (campaignId: number | string) =>
      `/campaigns/${campaignId}/schedule`,
    byId: (campaignId: number | string) => `/campaigns/${campaignId}`,
    delete: (campaignId: number | string) => `/campaigns/${campaignId}`,
    pause: (campaignId: number | string) => `/campaigns/${campaignId}/pause`,
    resume: (campaignId: number | string) => `/campaigns/${campaignId}/resume`,
    reschedule: (campaignId: number | string) =>
      `/campaigns/${campaignId}/reschedule`,
    export: (campaignId: number | string) => `/campaigns/${campaignId}/export`,
  },
  quickReplies: {
    base: "/quick-replies",
    byId: (quickReplyId: number | string) => `/quick-replies/${quickReplyId}`,
    suggest: "/quick-replies/suggest",
  },
  whatsappPayments: {
    base: "/whatsapp/payment-configurations",
    byId: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}`,
    byName: (configurationName: string) =>
      `/whatsapp/payment-configurations/name/${encodeURIComponent(configurationName)}`,
    identityCheck: "/whatsapp/payment-configurations/identityCheck",
    paymentStatusFlattenFields:
      "/whatsapp/payment-configurations/payment-status/flatten",
    paymentNotifications: "/payment-notifications",
    paymentNotificationById: (paymentStatusId: number | string) =>
      `/payment-notifications/${paymentStatusId}`,
    upsertPaymentNotification: "/payment-notifications/upsert",
    regenerateOAuth: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}/oauth-link/regenerate`,
    action: (configurationId: number | string) =>
      `/whatsapp/payment-configurations/${configurationId}/action`,
  },
  integrationApps: {
    oauth: {
      connect: (platform: string) =>
        `/oauth/connect/${encodeURIComponent(platform)}`,
      disconnect: "/oauth/disconnect",
      platforms: "/oauth/platforms",
      supported: (platform: string) =>
        `/oauth/platforms/${encodeURIComponent(platform)}/supported`,
      userDetails: "/oauth/user-details",
    },
    googleSpreadsheets: {
      base: "/google-apps/spreadsheets",
      byId: (spreadsheetId: string) =>
        `/google-apps/spreadsheets/${encodeURIComponent(spreadsheetId)}`,
      read: "/google-apps/spreadsheets/read",
      write: "/google-apps/spreadsheets/write",
      create: "/google-apps/spreadsheets/create",
    },
    googleCalendar: {
      calendars: "/google-calendar/calendars",
      calendarById: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}`,
      events: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      createEvent: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      eventById: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      updateEvent: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      deleteEvent: (calendarId: string, eventId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
      quickAddEvent: (calendarId: string) =>
        `/google-calendar/calendars/${encodeURIComponent(calendarId)}/events/quickAdd`,
      freeBusy: "/google-calendar/freebusy",
    },
    microsoftCalendar: {
      calendars: "/microsoft-calendar/calendars",
      calendarById: (calendarId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(calendarId)}`,
      events: (calendarId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(calendarId)}/events`,
      eventById: (calendarId: string, eventId: string) =>
        `/microsoft-calendar/calendars/${encodeURIComponent(
          calendarId,
        )}/events/${encodeURIComponent(eventId)}`,
    },
    microsoftTeamsMeetings: {
      base: "/microsoft-teams/meetings",
      byId: (meetingId: string) =>
        `/microsoft-teams/meetings/${encodeURIComponent(meetingId)}`,
      list: "/microsoft-teams/meetings/list",
    },
  },
  scheduling: {
    workspaces: {
      base: "/scheduling/workspaces",
      byId: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}`,
      members: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members`,
      membersBulk: (workspaceId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members/bulk`,
      memberById: (workspaceId: number | string, memberId: number | string) =>
        `/scheduling/workspaces/${workspaceId}/members/${memberId}`,
    },
    availabilitySchedules: {
      base: "/scheduling/availability-schedules",
      byId: (scheduleId: number | string) =>
        `/scheduling/availability-schedules/${scheduleId}`,
    },
    availabilityOverrides: {
      base: "/scheduling/availability-overrides",
      byId: (overrideId: number | string) =>
        `/scheduling/availability-overrides/${overrideId}`,
    },
    eventTypes: {
      base: "/scheduling/event-types",
      byId: (eventTypeId: number | string) =>
        `/scheduling/event-types/${eventTypeId}`,
      bySlug: (slug: string) =>
        `/scheduling/event-types/slug/${encodeURIComponent(slug)}`,
      byPublicKey: (publicKey: string) =>
        `/scheduling/event-types/public/${encodeURIComponent(publicKey)}`,
    },
    routingForms: {
      base: "/scheduling/routing-forms",
      byId: (routingFormId: number | string) =>
        `/scheduling/routing-forms/${routingFormId}`,
    },
    insights: {
      bookingMemberStats: "/scheduling/insights/bookings/member-stats",
      bookingEventTrends: "/scheduling/insights/bookings/event-trends",
    },
    bookings: {
      base: "/scheduling/bookings",
      byUid: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}`,
      cancel: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/cancel`,
      confirm: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/confirm`,
      decline: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/decline`,
      markAbsent: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/mark-absent`,
      requestReschedule: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(
          bookingUid,
        )}/request-reschedule`,
      reschedule: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/reschedule`,
      postMeeting: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/post-meeting`,
      recordings: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/recordings`,
      transcripts: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/transcripts`,
      transcriptSegments: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(
          bookingUid,
        )}/transcripts/segments`,
      participants: (bookingUid: string) =>
        `/scheduling/bookings/${encodeURIComponent(bookingUid)}/participants`,
    },
    public: {
      bookingLinks: {
        byPublicKey: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(publicKey)}`,
        slots: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(publicKey)}/slots`,
        availabilityDates: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/availability-dates`,
        reserveSlot: (publicKey: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/reserve-slot`,
        reschedule: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}/reschedule`,
        cancel: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}/cancel`,
        bookingDetails: (publicKey: string, bookingId: string) =>
          `/scheduling/booking-links/${encodeURIComponent(
            publicKey,
          )}/bookings/${encodeURIComponent(bookingId)}`,
      },
      bookings: {
        create: "/scheduling/bookings/public",
        sendEmailVerification: "/scheduling/bookings/email-verification/send",
      },
    },
  },
  workflows: {
    /** GET — list events that can trigger a workflow */
    events: "/workflows/events",
    /** GET — list fields for a workflow event */
    eventFields: (eventId: number | string) =>
      `/workflows/events/${eventId}/fields`,
    /** DELETE — delete an event by id */
    byEventId: (eventId: number | string) => `/workflows/events/${eventId}`,
    /** POST — onboard a custom event + webhook action */
    onboardEvent: "/workflows/onboard-event",
    /** GET (list), POST (create) — base workflows endpoint */
    base: "/workflows",
    /** GET (by id), PATCH (update by id) */
    byId: (workflowId: number | string) => `/workflows/${workflowId}`,
    /** PATCH — change a single workflow's status */
    status: (workflowId: number | string) => `/workflows/${workflowId}/status`,
    /** PATCH — bulk soft delete / restore workflows */
    bulkSoftDelete: "/workflows/delete",
    /** DELETE — bulk hard delete workflows */
    bulkHardDelete: "/workflows/bulk",
  },
  whatsappFlows: {
    base: "/whatsapp/flows",
    list: "/whatsapp/flows",
    byId: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    create: "/whatsapp/flows/create",
    save: "/whatsapp/flows",
    /** PUT /whatsapp/flows/{flowId} — updates name, categories, flowAction, dataEndpointUrl */
    update: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    updateMetadata: "/whatsapp/flows/update/metadata",
    updateJsonMultipart: "/whatsapp/flows/update/json",
    updateJson: (flowId: number | string) => `/whatsapp/flows/${flowId}/json`,
    publish: (flowId: number | string) => `/whatsapp/flows/${flowId}/publish`,
    deprecate: (flowId: number | string) =>
      `/whatsapp/flows/${flowId}/deprecate`,
    delete: "/whatsapp/flows/delete",
    deleteById: (flowId: number | string) => `/whatsapp/flows/${flowId}`,
    responses: "/whatsapp/flows/responses",
    responsesExport: (flowId: number | string) =>
      `/whatsapp-flows/${flowId}/responses/export`,
  },
} as const;

// Environment types
export const ENVIRONMENTS = {
  DEV: "dev",
  PROD: "prod",
} as const;

export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

// Environment-specific configurations
type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];
const ENV: Environment =
  (import.meta.env.VITE_ENVIRONMENT as Environment) || ENVIRONMENTS.DEV;
const environmentConfigs = {
  [ENVIRONMENTS.DEV]: {
    APP_URL: "https://app-new-dev.notifyy.io",
    PUBLIC_BOOKING_URL: "https://meetings-dev.notifyy.io",
    BASE_URL: "https://crm-dev.notifyy.io/v3/api/",
    OBJECT_STORAGE_URL: "https://object-dev.notifyy.io/v3/api/",
    AZURE_BLOB_BASE_URL:
      "https://notifyydevobjstorage02.blob.core.windows.net/notifyydevobjstorageblob02/",
    CAMPAIGN_BASE_URL: "https://scheduler-dev.notifyy.io/v3/api/",
    CRM_URL: "https://crm-dev.notifyy.io/v3/api/",
    PUBLIC_BASE_URL: "https://crm-dev.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer-dev.notifyy.io/ws-chat-gateway",
    NOTIFICATION_SOCKET_URL: "https://consumer-dev.notifyy.io/ws-notifications",
    CONSUMER_BASE_URL: "https://consumer-dev.notifyy.io",
    // AI_BASE_URL: "https://ai-dev.notifyy.io/api/v1/",
    AI_BASE_URL: "http://localhost:8000/api/v1/",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: true,
    API_TIMEOUT: 30000,
    AI_API_TIMEOUT: 180000,
    RETRY_ATTEMPTS: 5,
    ENV_NAME: "Development",
  },
  [ENVIRONMENTS.PROD]: {
    // APP_URL: "https://app-new.notifyy.io",
    APP_URL: "https://app.notifyy.io",
    PUBLIC_BOOKING_URL: "https://meetings.notifyy.io",
    BASE_URL: "https://crm.notifyy.io/v3/api/",
    OBJECT_STORAGE_URL: "https://object.notifyy.io/v3/api/",
    AZURE_BLOB_BASE_URL:
      "https://notifyyprodobjstorage02.blob.core.windows.net/notifyyprodobjstorageblob02/",
    CAMPAIGN_BASE_URL: "https://scheduler.notifyy.io/v3/api/",
    CRM_URL: "https://crm.notifyy.io/v3/api/",
    PUBLIC_BASE_URL: "https://crm.notifyy.io/v3/api/",
    META_BASE_URL: "https://graph.facebook.com/v21.0/",
    WEB_SOCKET_URL: "wss://consumer.notifyy.io/ws-chat-gateway",
    NOTIFICATION_SOCKET_URL: "https://consumer.notifyy.io/ws-notifications",
    CONSUMER_BASE_URL: "https://consumer.notifyy.io",
    AI_BASE_URL: "https://ai.notifyy.io/api/v1/",
    WIDGET_SCRIPT_URL: "https://cdn.notifyy-widget.pages.dev/widget-plugin.js",
    DEBUG_MODE: false,
    API_TIMEOUT: 30000,
    AI_API_TIMEOUT: 180000,
    RETRY_ATTEMPTS: 5,
    ENV_NAME: "Production",
  },
} as const;

// Current environment config
const config = environmentConfigs[ENV];

const getLocalPublicBookingOrigin = (): string => {
  if (!import.meta.env.DEV || typeof window === "undefined") return "";

  const hostname = window.location.hostname.toLowerCase();
  const isLocalHost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost");

  return isLocalHost ? window.location.origin : "";
};

const parsePositiveInteger = (
  value: string | undefined,
  fallback: number,
): number => {
  if (!value) return fallback;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallback;
};

// API Base URLs
export const API_URLS = {
  app: config.APP_URL,
  publicBooking:
    import.meta.env.VITE_PUBLIC_BOOKING_URL ||
    getLocalPublicBookingOrigin() ||
    config.PUBLIC_BOOKING_URL,
  main: config.BASE_URL,
  public: import.meta.env.VITE_PUBLIC_API_BASE_URL || config.PUBLIC_BASE_URL,
  objectStorage: config.OBJECT_STORAGE_URL,
  azureBlob: config.AZURE_BLOB_BASE_URL,
  campaign: config.CAMPAIGN_BASE_URL,
  crm: config.CRM_URL,
  meta: config.META_BASE_URL,
  websocket: config.WEB_SOCKET_URL,
  notification: config.NOTIFICATION_SOCKET_URL,
  consumer: config.CONSUMER_BASE_URL,
  ai: config.AI_BASE_URL,
  widgetScript: config.WIDGET_SCRIPT_URL,
} as const;

export const resolveAzureBlobFileUrl = (
  fileUrl?: string | null,
): string | null => {
  const trimmedFileUrl = fileUrl?.trim();
  if (!trimmedFileUrl) return null;

  if (/^(https?:|blob:|data:)/i.test(trimmedFileUrl)) {
    return trimmedFileUrl;
  }

  const baseUrl = API_URLS.azureBlob.replace(/\/+$/, "");
  const filePath = trimmedFileUrl.replace(/^\/+/, "");
  return encodeURI(`${baseUrl}/${filePath}`);
};

// API Settings
export const API_SETTINGS = {
  timeout: config.API_TIMEOUT,
  aiTimeout: parsePositiveInteger(
    import.meta.env.VITE_AI_API_TIMEOUT_MS,
    config.AI_API_TIMEOUT,
  ),
  retries: config.RETRY_ATTEMPTS,
  debugMode: config.DEBUG_MODE,
  envName: config.ENV_NAME,
  aiApiKey:
    import.meta.env.VITE_NOTIFY_AI_API_KEY ||
    import.meta.env.VITE_AI_API_KEY ||
    "",
} as const;

// Auth Retry Configuration
export const AUTH_RETRY_CONFIG = {
  /** Maximum number of retry attempts before forcing logout */
  MAX_RETRIES: 7,
  /** Initial delay between retries in milliseconds */
  INITIAL_DELAY_MS: 1000,
  /** Maximum delay between retries in milliseconds */
  MAX_DELAY_MS: 10000,
  /** Backoff multiplier for exponential delay */
  BACKOFF_MULTIPLIER: 1.5,
} as const;

// Error Messages
export const API_ERRORS = {
  NETWORK: "Unable to connect. Please check your internet.",
  TIMEOUT: "Request timed out. Please try again.",
  SERVER: "Server error. Please try again later.",
  UNAUTHORIZED: "Session expired. Please login again.",
  FORBIDDEN: "You do not have permission.",
  NOT_FOUND: "Resource not found.",
  RATE_LIMIT: "Too many requests. Please slow down.",
  UNKNOWN: "Something went wrong.",
} as const;

// Export types
export type ApiUrlKey = keyof typeof API_URLS;
export type EnvironmentConfig = typeof config;

// Export current environment
export const CURRENT_ENV = ENV;
export const IS_DEV = ENV === ENVIRONMENTS.DEV;
export const IS_PROD = ENV === ENVIRONMENTS.PROD;

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  REFRESH_TOKEN: "refresh_token",
  USER_DATA: "user_data",
  THEME: "theme",
  PERSIST_ROOT: "persist:root",
} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  ENABLE_ENCRYPTION: true,
  TOKEN_STORAGE: "localStorage" as const,
} as const;

// Google OAuth Configuration
export const GOOGLE_OAUTH_CONFIG = {
  clientId:
    "746540176547-dq7ogmnjqmurhu9uppuamr2mfmjtbftt.apps.googleusercontent.com",
  redirectUri: `${API_URLS.main}${API_ENDPOINTS.auth.googleOAuthCallback.slice(1)}`,
  scope: "openid email profile",
  accessType: "offline",
  prompt: "consent",
} as const;

// Microsoft OAuth Configuration (MSAL)
export const MICROSOFT_OAUTH_CONFIG = {
  clientId: "32c1f1b1-b3e5-4197-88a2-24851c4bd92f",
  authority: "https://login.microsoftonline.com/common",
  redirectUri: `${API_URLS.main}${API_ENDPOINTS.auth.microsoftOAuthCallback.slice(1)}`,
  postLogoutRedirectUri: window.location.origin,
  scopes: ["openid", "profile", "email"],
  responseMode: "query",
} as const;

export const buildMicrosoftOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
  invitationToken?: string,
): string => {
  const nonce =
    crypto.randomUUID?.() ||
    `${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;

  // For agent/invitation callback, the backend expects the invitation JWT token as the state
  const state =
    useAgentCallback && invitationToken
      ? invitationToken
      : JSON.stringify({
          nonce: nonce.substring(0, 15),
          provider: "microsoft",
          ...(frontendCallbackUrl && { redirectUri: frontendCallbackUrl }),
          ...(fromPath && { from: fromPath }),
        });

  // Use agent callback redirect URI for invitation signup
  const redirectUri = useAgentCallback
    ? `${API_URLS.main}${API_ENDPOINTS.auth.microsoftOAuthAgentCallback.slice(1)}`
    : MICROSOFT_OAUTH_CONFIG.redirectUri;

  const params = new URLSearchParams({
    client_id: MICROSOFT_OAUTH_CONFIG.clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    response_mode: MICROSOFT_OAUTH_CONFIG.responseMode,
    scope: MICROSOFT_OAUTH_CONFIG.scopes.join(" "),
    state,
    nonce,
  });

  return `${MICROSOFT_OAUTH_CONFIG.authority}/oauth2/v2.0/authorize?${params.toString()}`;
};

export const buildGoogleOAuthUrl = (
  frontendCallbackUrl?: string,
  fromPath?: string,
  useAgentCallback?: boolean,
  invitationToken?: string,
): string => {
  // Use agent callback redirect URI for invitation signup
  const redirectUri = useAgentCallback
    ? `${API_URLS.main}${API_ENDPOINTS.auth.googleOAuthAgentCallback.slice(1)}`
    : GOOGLE_OAUTH_CONFIG.redirectUri;

  const nonce =
    crypto.randomUUID?.() ||
    `${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;

  const params = new URLSearchParams({
    client_id: GOOGLE_OAUTH_CONFIG.clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: GOOGLE_OAUTH_CONFIG.scope,
    access_type: GOOGLE_OAUTH_CONFIG.accessType,
    prompt: GOOGLE_OAUTH_CONFIG.prompt,
  });

  // For agent/invitation callback, the backend expects the invitation JWT token as the state
  if (useAgentCallback && invitationToken) {
    params.set("state", invitationToken);
  } else {
    const stateObj: Record<string, string> = {
      nonce: nonce.substring(0, 15),
      provider: "google",
    };
    if (frontendCallbackUrl) {
      stateObj.redirectUri = frontendCallbackUrl;
    }
    if (fromPath) {
      stateObj.from = fromPath;
    }
    params.set("state", JSON.stringify(stateObj));
  }
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

export const SHOPIFY_API_KEY = "726f5bbb730de75d4dec518c1c8679a3";
